import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Calendar, Clock, Plus, Trash2, LogOut, Film, History, ChevronDown, ChevronLeft, ChevronRight, Radio, User, Building2, MapPin, Monitor, Phone, Mail, Save, AlertCircle, Check, Users, Truck, FileText, Edit, X } from 'lucide-react';
import BoxOfficeReports from '../components/BoxOfficeReports';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_90b4c8a6-2372-4e9e-bd52-ac51039d9d6b/artifacts/l6uwsqcm_logo_bravo_cinemastreaming.png";

// Categories for filtering
const CATEGORIES = [
  { value: 'all', label: 'Todos' },
  { value: 'ÒPERA', label: 'Óperas' },
  { value: 'BALLET', label: 'Ballet' },
  { value: 'CONCERT', label: 'Conciertos' },
  { value: 'DOCUMENTAL', label: 'Documentales' },
  { value: 'PELÍCULA', label: 'Películas' }
];

// Category display names (for badges)
const CATEGORY_LABELS = {
  'ÒPERA': 'Ópera',
  'BALLET': 'Ballet',
  'CONCERT': 'Concierto',
  'DOCUMENTAL': 'Documental',
  'PELÍCULA': 'Película'
};

// Helper function to get category label
const getCategoryLabel = (category) => CATEGORY_LABELS[category] || category;

const CinemaBookingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cinema, setCinema] = useState(null);
  const [step, setStep] = useState(1);
  const [selectedContent, setSelectedContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [myBookings, setMyBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('new');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [allContents, setAllContents] = useState([]);
  const [loadingContents, setLoadingContents] = useState(true);
  
  // Simplified screening data - just date and times
  const [screeningDate, setScreeningDate] = useState('');
  const [screeningTimes, setScreeningTimes] = useState(['']);
  const [marketingSupport, setMarketingSupport] = useState(false);
  const [dateError, setDateError] = useState(''); // Track date validation error

  // Account editing state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaveStatus, setProfileSaveStatus] = useState(null);
  
  // Cinema pricing state
  const [contentPrice, setContentPrice] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [cinemaPricing, setCinemaPricing] = useState(null);  // General cinema conditions including Bravo rental
  
  // Calendar state for programming calendar
  const [calendarDate, setCalendarDate] = useState(new Date());

  // Inquiries state (special conditions)
  const [myInquiries, setMyInquiries] = useState([]);
  const [loadingInquiries, setLoadingInquiries] = useState(false);
  const [sendingInquiry, setSendingInquiry] = useState(false);
  const [inquirySuccess, setInquirySuccess] = useState(false);

  // Notification states for new offers
  const [showOfferToast, setShowOfferToast] = useState(false);
  const [newOffersCount, setNewOffersCount] = useState(0);
  const [hasUnseenOffers, setHasUnseenOffers] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  // Box office reports state
  const [pendingReportsCount, setPendingReportsCount] = useState(0);
  
  // Booking change request states
  const [showCancelModal, setShowCancelModal] = useState(null);
  const [showModifyModal, setShowModifyModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [modifyData, setModifyData] = useState({ screenings: [], reason: '' });
  const [submittingChange, setSubmittingChange] = useState(false);
  const [changeRequestSuccess, setChangeRequestSuccess] = useState(null); // {type: 'modification'|'cancellation', message: string}
  
  // Venue selector state (for multi-venue accounts)
  const [selectedVenueIndex, setSelectedVenueIndex] = useState(0);
  const [showVenueSelector, setShowVenueSelector] = useState(false);
  
  // Push notifications state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);

  // Add new venue modal state
  const [showAddVenueModal, setShowAddVenueModal] = useState(false);
  const [newVenueData, setNewVenueData] = useState({
    venue_name: '',
    address: {
      street: '',
      city: '',
      postal_code: '',
      province: '',
      country: 'España'
    },
    rooms: [{
      room_number: '',
      seating_capacity: '',
      projection_system: 'DCP',
      sound_system: 'Dolby Digital 5.1',
      internet_connection: 'Fibra Óptica'
    }]
  });
  const [addingVenue, setAddingVenue] = useState(false);
  const [addVenueError, setAddVenueError] = useState('');
  const [addVenueSuccess, setAddVenueSuccess] = useState(false);

  // Constants for venue form
  const projectionSystems = ['DCP', 'Digital HD', 'Digital 4K'];
  const soundSystems = ['Estéreo', 'Dolby Digital 5.1', 'Dolby Digital 7.1'];
  const internetConnections = ['ADSL', 'Fibra Óptica', '5G'];

  // Check for new offers and trigger notifications
  useEffect(() => {
    const respondedInquiries = myInquiries.filter(i => i.status === 'responded');
    const seenOffers = JSON.parse(localStorage.getItem('seen_offers') || '[]');
    const unseenOffers = respondedInquiries.filter(i => !seenOffers.includes(i.id));
    
    if (unseenOffers.length > 0) {
      setNewOffersCount(unseenOffers.length);
      setHasUnseenOffers(true);
      
      // Show toast with content name
      const content = allContents.find(c => c.id === unseenOffers[0].content_id);
      const contentName = content?.title || 'contenido';
      setToastMessage(
        unseenOffers.length === 1 
          ? `🎉 ¡Nueva oferta para "${contentName}"!`
          : `🎉 ¡Tienes ${unseenOffers.length} ofertas nuevas!`
      );
      setShowOfferToast(true);
      
      // Play notification sound
      playNotificationSound();
      
      // Auto-hide toast after 6 seconds
      setTimeout(() => setShowOfferToast(false), 6000);
    }
  }, [myInquiries, allContents]);

  // Mark offers as seen when viewing "Mis solicitudes"
  useEffect(() => {
    if (activeTab === 'pending' && hasUnseenOffers) {
      const respondedIds = myInquiries.filter(i => i.status === 'responded').map(i => i.id);
      localStorage.setItem('seen_offers', JSON.stringify(respondedIds));
      setHasUnseenOffers(false);
      setNewOffersCount(0);
    }
  }, [activeTab, hasUnseenOffers, myInquiries]);

  // Notification sound function
  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Pleasant notification sound (two-tone chime)
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
      oscillator.frequency.setValueAtTime(1100, audioContext.currentTime + 0.1); // C#6
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.4);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  // Load contents from backend
  useEffect(() => {
    const loadContents = async () => {
      setLoadingContents(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/content`);
        if (res.ok) {
          const data = await res.json();
          // Map snake_case to camelCase and include all fields
          const mapped = data.map(c => ({
            ...c,
            heroImage: c.hero_image || c.image,
            trailerUrl: c.trailer_url,
            premiereDate: c.premiere_date,
            artisticCredits: c.artistic_credits || [],
            productInfo: c.product_info,
            // Live streaming fields - ensure they are included
            is_live: c.is_live || false,
            live_date: c.live_date || '',
            live_time: c.live_time || ''
          }));
          setAllContents(mapped);
          
          // Check if we have a pre-selected contentId from navigation state or URL query param
          const urlParams = new URLSearchParams(location.search);
          const preSelectedId = location.state?.contentId || urlParams.get('content');
          if (preSelectedId) {
            const preSelected = mapped.find(c => c.id === preSelectedId);
            if (preSelected) {
              setSelectedContent(preSelected);
              setStep(2);
            }
          }
        }
      } catch (err) {
        console.error('Error loading contents:', err);
      }
      setLoadingContents(false);
    };
    loadContents();
  }, [location.state, location.search]);

  useEffect(() => {
    const session = localStorage.getItem('cinema_session');
    if (!session) { navigate('/cinema/login'); return; }
    const data = JSON.parse(session);
    setCinema(data);
    loadBookings(data.cinema_id);
    loadFullProfile(data.cinema_id);
    loadInquiries(data.cinema_id);
    loadPendingReportsCount(data.cinema_id);
    
    // Load saved venue selection
    const savedVenueIndex = localStorage.getItem(`selected_venue_${data.cinema_id}`);
    if (savedVenueIndex !== null) {
      setSelectedVenueIndex(parseInt(savedVenueIndex, 10));
    }
    
    // Check push notification support
    checkPushSupport();
  }, [navigate]);

  // Check if push notifications are supported
  const checkPushSupport = () => {
    if ('Notification' in window && 'serviceWorker' in navigator) {
      setPushSupported(true);
      setPushPermission(Notification.permission);
      // Check if already subscribed
      const subscribed = localStorage.getItem('push_subscribed') === 'true';
      setPushSubscribed(subscribed);
    }
  };

  // Request push notification permission
  const requestPushPermission = async () => {
    if (!pushSupported) return;
    
    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      
      if (permission === 'granted') {
        // Register service worker if not already registered
        const registration = await navigator.serviceWorker.register('/sw.js');
        
        // Subscribe to push (in a real app, you'd send this to your backend)
        localStorage.setItem('push_subscribed', 'true');
        setPushSubscribed(true);
        
        // Show a test notification
        new Notification('¡Notificaciones activadas!', {
          body: 'Recibirás alertas cuando tus solicitudes sean procesadas.',
          icon: LOGO_URL
        });
      }
    } catch (err) {
      console.error('Error requesting push permission:', err);
    }
  };

  // Send a local push notification
  const sendLocalNotification = (title, body) => {
    if (pushPermission === 'granted' && pushSubscribed) {
      new Notification(title, {
        body,
        icon: LOGO_URL,
        badge: LOGO_URL
      });
    }
  };

  // Handle venue selection change
  const handleVenueChange = (venueIndex) => {
    setSelectedVenueIndex(venueIndex);
    setShowVenueSelector(false);
    if (cinema) {
      localStorage.setItem(`selected_venue_${cinema.cinema_id}`, venueIndex.toString());
    }
  };

  // Get current selected venue
  const getSelectedVenue = () => {
    if (!profileData?.venues || profileData.venues.length === 0) {
      // Legacy: return venue_name from profile
      return { venue_name: profileData?.venue_name || cinema?.venue_name || 'Mi Cine' };
    }
    return profileData.venues[selectedVenueIndex] || profileData.venues[0];
  };

  // New venue form handlers
  const updateNewVenueField = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setNewVenueData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setNewVenueData(prev => ({ ...prev, [field]: value }));
    }
  };

  const updateNewVenueRoom = (roomIndex, field, value) => {
    setNewVenueData(prev => {
      const newRooms = [...prev.rooms];
      newRooms[roomIndex] = { ...newRooms[roomIndex], [field]: value };
      return { ...prev, rooms: newRooms };
    });
  };

  const addNewVenueRoom = () => {
    setNewVenueData(prev => ({
      ...prev,
      rooms: [...prev.rooms, {
        room_number: '',
        seating_capacity: '',
        projection_system: 'DCP',
        sound_system: 'Dolby Digital 5.1',
        internet_connection: 'Fibra Óptica'
      }]
    }));
  };

  const removeNewVenueRoom = (roomIndex) => {
    if (newVenueData.rooms.length > 1) {
      setNewVenueData(prev => ({
        ...prev,
        rooms: prev.rooms.filter((_, i) => i !== roomIndex)
      }));
    }
  };

  const resetNewVenueForm = () => {
    setNewVenueData({
      venue_name: '',
      address: { street: '', city: '', postal_code: '', province: '', country: 'España' },
      rooms: [{ room_number: '', seating_capacity: '', projection_system: 'DCP', sound_system: 'Dolby Digital 5.1', internet_connection: 'Fibra Óptica' }]
    });
    setAddVenueError('');
    setAddVenueSuccess(false);
  };

  const handleAddVenue = async () => {
    // Validation
    if (!newVenueData.venue_name.trim()) {
      setAddVenueError('El nombre del recinto es obligatorio');
      return;
    }
    if (!newVenueData.address.city.trim()) {
      setAddVenueError('La ciudad es obligatoria');
      return;
    }

    setAddingVenue(true);
    setAddVenueError('');

    try {
      const res = await fetch(`${BACKEND_URL}/api/cinemas/${cinema.cinema_id}/venues`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newVenueData)
      });

      if (res.ok) {
        const data = await res.json();
        setAddVenueSuccess(true);
        // Reload profile to get updated venues
        const profileRes = await fetch(`${BACKEND_URL}/api/cinemas/${cinema.cinema_id}/profile`);
        if (profileRes.ok) {
          const profileData = await profileRes.json();
          setProfileData(profileData);
        }
        // Close modal after showing success
        setTimeout(() => {
          setShowAddVenueModal(false);
          resetNewVenueForm();
        }, 2000);
      } else {
        const error = await res.json();
        setAddVenueError(error.detail || 'Error al añadir el recinto');
      }
    } catch (err) {
      setAddVenueError('Error de conexión. Inténtalo de nuevo.');
    } finally {
      setAddingVenue(false);
    }
  };

  // Load pending box office reports count
  const loadPendingReportsCount = async (cinemaId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/box-office-reports/pending-count?cinema_id=${cinemaId}`);
      if (res.ok) {
        const data = await res.json();
        setPendingReportsCount(data.pending_count || 0);
      }
    } catch (err) {
      console.error('Error loading pending reports:', err);
    }
  };

  // Submit booking cancellation request
  const handleCancelRequest = async (booking) => {
    if (!cancelReason.trim()) {
      setChangeRequestSuccess({ type: 'error', message: 'Por favor, indica el motivo de la cancelación' });
      setTimeout(() => setChangeRequestSuccess(null), 4000);
      return;
    }
    setSubmittingChange(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/booking-change-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: booking.id,
          cinema_id: cinema.cinema_id,
          request_type: 'cancellation',
          reason: cancelReason
        })
      });
      if (res.ok) {
        setShowCancelModal(null);
        setCancelReason('');
        setChangeRequestSuccess({ 
          type: 'success', 
          message: 'Solicitud de cancelación enviada correctamente. El administrador revisará tu petición.' 
        });
        // Reload bookings to show updated status
        if (cinema?.cinema_id) {
          await loadBookings(cinema.cinema_id);
        }
        setTimeout(() => setChangeRequestSuccess(null), 5000);
      } else {
        const err = await res.json();
        setChangeRequestSuccess({ type: 'error', message: err.detail || 'Error al enviar la solicitud' });
        setTimeout(() => setChangeRequestSuccess(null), 5000);
      }
    } catch (err) {
      setChangeRequestSuccess({ type: 'error', message: 'Error de conexión. Inténtalo de nuevo.' });
      setTimeout(() => setChangeRequestSuccess(null), 5000);
    }
    setSubmittingChange(false);
  };

  // Submit booking modification request
  const handleModifyRequest = async (booking) => {
    if (!modifyData.screenings.length) {
      setChangeRequestSuccess({ type: 'error', message: 'Por favor, indica las nuevas fechas y horarios' });
      setTimeout(() => setChangeRequestSuccess(null), 4000);
      return;
    }
    
    // Validate minimum 2 days in advance for all screenings
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() + 2);
    
    for (const screening of modifyData.screenings) {
      if (screening.date) {
        const screeningDate = new Date(screening.date + 'T00:00:00');
        if (screeningDate < minDate) {
          setChangeRequestSuccess({ type: 'error', message: 'La fecha de proyección debe ser al menos 2 días a partir de hoy' });
          setTimeout(() => setChangeRequestSuccess(null), 4000);
          return;
        }
      }
    }
    
    setSubmittingChange(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/booking-change-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: booking.id,
          cinema_id: cinema.cinema_id,
          request_type: 'modification',
          new_screenings: modifyData.screenings,
          reason: modifyData.reason
        })
      });
      if (res.ok) {
        setShowModifyModal(null);
        setModifyData({ screenings: [], reason: '' });
        setChangeRequestSuccess({ 
          type: 'success', 
          message: 'Solicitud de modificación enviada correctamente. El administrador revisará tu petición.' 
        });
        // Reload bookings to show updated status
        if (cinema?.cinema_id) {
          await loadBookings(cinema.cinema_id);
        }
        setTimeout(() => setChangeRequestSuccess(null), 5000);
      } else {
        const err = await res.json();
        setChangeRequestSuccess({ type: 'error', message: err.detail || 'Error al enviar la solicitud' });
        setTimeout(() => setChangeRequestSuccess(null), 5000);
      }
    } catch (err) {
      setChangeRequestSuccess({ type: 'error', message: 'Error de conexión. Inténtalo de nuevo.' });
      setTimeout(() => setChangeRequestSuccess(null), 5000);
    }
    setSubmittingChange(false);
  };

  // Check if booking can be modified (based on content type)
  const canModifyBooking = (booking) => {
    const content = allContents.find(c => c.id === booking.content_id);
    if (!content) return false;
    // Live events cannot be modified, only cancelled
    if (content.is_live) return false;
    return true;
  };

  // Load full cinema profile for account tab
  const loadFullProfile = async (cinemaId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cinemas/${cinemaId}`);
      if (res.ok) {
        const data = await res.json();
        setProfileData(data);
      }
      // Also load cinema pricing/conditions
      const pricingRes = await fetch(`${BACKEND_URL}/api/cinema-pricing/${cinemaId}`);
      if (pricingRes.ok) {
        const pricingData = await pricingRes.json();
        setCinemaPricing(pricingData);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    }
  };

  const loadBookings = async (cinemaId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/bookings?cinema_id=${cinemaId}`);
      if (res.ok) {
        const data = await res.json();
        setMyBookings(data);
      }
    } catch (err) {
      console.error('Error loading bookings:', err);
    }
  };

  // Load inquiries for this cinema
  const loadInquiries = async (cinemaId) => {
    setLoadingInquiries(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/inquiries?cinema_id=${cinemaId}`);
      if (res.ok) {
        const data = await res.json();
        setMyInquiries(data);
      }
    } catch (err) {
      console.error('Error loading inquiries:', err);
    }
    setLoadingInquiries(false);
  };

  // Create an inquiry for content with special conditions
  const createInquiry = async (contentId) => {
    if (!cinema) return;
    setSendingInquiry(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cinema_id: cinema.cinema_id,
          content_id: contentId
        })
      });
      
      if (res.ok) {
        setInquirySuccess(true);
        loadInquiries(cinema.cinema_id);
        // Reset after showing success
        setTimeout(() => {
          setInquirySuccess(false);
          reset();
        }, 2000);
      } else {
        const data = await res.json();
        setError(data.detail || 'Error al enviar la consulta');
      }
    } catch (err) {
      setError('Error de conexión');
    }
    setSendingInquiry(false);
  };

  // Accept inquiry offer and create booking
  const acceptInquiry = async (inquiryId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/inquiries/${inquiryId}/accept`, {
        method: 'PUT'
      });
      
      if (res.ok) {
        const data = await res.json();
        // Show success message
        setSuccess(true);
        setError('');
        // Reload inquiries and bookings
        loadInquiries(cinema.cinema_id);
        loadBookings(cinema.cinema_id);
        // Reset success after delay
        setTimeout(() => setSuccess(false), 3000);
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Error al aceptar la oferta');
      }
    } catch (err) {
      console.error('Error accepting inquiry:', err);
      setError('Error de conexión');
    }
  };

  // Reject inquiry offer
  const rejectInquiry = async (inquiryId) => {
    if (!window.confirm('¿Estás seguro de rechazar esta oferta? Esta acción no se puede deshacer.')) {
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/inquiries/${inquiryId}/reject`, {
        method: 'PUT'
      });
      
      if (res.ok) {
        loadInquiries(cinema.cinema_id);
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Error al rechazar la oferta');
      }
    } catch (err) {
      console.error('Error rejecting inquiry:', err);
      setError('Error de conexión');
    }
  };

  // Load content price for this cinema
  const loadContentPrice = async (cinemaId, contentId) => {
    setLoadingPrice(true);
    setContentPrice(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cinema-pricing/${cinemaId}/content/${contentId}`);
      if (res.ok) {
        const data = await res.json();
        setContentPrice(data);
      }
    } catch (err) {
      console.error('Error loading content price:', err);
    }
    setLoadingPrice(false);
  };

  // Load price when content is selected
  useEffect(() => {
    if (selectedContent && cinema) {
      loadContentPrice(cinema.cinema_id, selectedContent.id);
    }
  }, [selectedContent, cinema]);

  // Save profile changes (sends to pending_changes for admin validation)
  const handleSaveProfile = async () => {
    if (!profileData || !cinema) return;
    setSavingProfile(true);
    setProfileSaveStatus(null);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/cinemas/${cinema.cinema_id}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: profileData.company_name,
          cif: profileData.cif,
          contact_name: profileData.contact_name,
          contact_phone: profileData.contact_phone,
          venue_name: profileData.venue_name,
          address: profileData.address,
          technical_specs: profileData.technical_specs
        })
      });
      
      if (res.ok) {
        setProfileSaveStatus('success');
        setEditingProfile(false);
        // Reload profile to get updated pending_changes status
        loadFullProfile(cinema.cinema_id);
      } else {
        setProfileSaveStatus('error');
      }
    } catch (err) {
      setProfileSaveStatus('error');
    }
    setSavingProfile(false);
  };

  const addTime = () => setScreeningTimes([...screeningTimes, '']);
  const removeTime = (i) => screeningTimes.length > 1 && setScreeningTimes(screeningTimes.filter((_, idx) => idx !== i));
  const updateTime = (i, value) => setScreeningTimes(screeningTimes.map((t, idx) => idx === i ? value : t));

  const handleSubmit = async () => {
    if (!selectedContent || !cinema) return;
    setLoading(true); setError('');
    
    // Note: Content with future premiere_date can be booked, but the screening date
    // must be on or after the premiere_date (validated below in the screening date section)
    
    let screenings;
    
    // For live content, use the fixed date/time from content
    if (selectedContent.is_live && selectedContent.live_date) {
      screenings = [{
        date: selectedContent.live_date,
        time: selectedContent.live_time || '20:00'
      }];
    } else {
      // For regular content, build screenings from user input
      if (!screeningDate) {
        setError('Selecciona una fecha');
        setLoading(false);
        return;
      }
      
      // Validate minimum 2 days in advance
      const selectedDateObj = new Date(screeningDate + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const minDate = new Date(today);
      minDate.setDate(minDate.getDate() + 2);
      
      if (selectedDateObj < minDate) {
        setError('La fecha de proyección debe ser al menos 2 días a partir de hoy');
        setLoading(false);
        return;
      }
      
      // Validate encore date range
      if (selectedContent.is_encore && selectedContent.encore_start_date && selectedContent.encore_end_date) {
        const startDate = new Date(selectedContent.encore_start_date);
        const endDate = new Date(selectedContent.encore_end_date);
        
        if (selectedDateObj < startDate || selectedDateObj > endDate) {
          setError(`Para contenido ENCORE, la fecha debe estar entre ${new Date(selectedContent.encore_start_date).toLocaleDateString('es-ES')} y ${new Date(selectedContent.encore_end_date).toLocaleDateString('es-ES')}`);
          setLoading(false);
          return;
        }
      }
      
      // For coming_soon content, validate that screening date is after premiere date
      if (selectedContent.status === 'coming_soon' && selectedContent.premiere_date) {
        const premiereDate = new Date(selectedContent.premiere_date);
        
        if (selectedDateObj < premiereDate) {
          setError(`La fecha de proyección debe ser a partir del ${new Date(selectedContent.premiere_date).toLocaleDateString('es-ES')}`);
          setLoading(false);
          return;
        }
      }
      
      screenings = screeningTimes
        .filter(t => t) // Only non-empty times
        .map(time => ({ date: screeningDate, time }));
      
      if (screenings.length === 0) {
        setError('Añade al menos un horario');
        setLoading(false);
        return;
      }
    }

    // Get selected venue info
    const selectedVenue = getSelectedVenue();
    const venueInfo = {
      venue_index: selectedVenueIndex,
      venue_name: selectedVenue.venue_name || profileData?.venue_name || cinema?.venue_name
    };

    try {
      const res = await fetch(`${BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cinema_id: cinema.cinema_id,
          content_id: selectedContent.id,
          screenings,
          marketing_support_needed: marketingSupport,
          venue_name: venueInfo.venue_name,
          venue_index: venueInfo.venue_index
        })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Error al enviar solicitud');
      }
      
      setSuccess(true);
      loadBookings(cinema.cinema_id);
    } catch (e) { 
      setError(e.message); 
    }
    setLoading(false);
  };

  const reset = () => {
    setSelectedContent(null);
    setStep(1);
    setScreeningDate('');
    setScreeningTimes(['']);
    setMarketingSupport(false);
    setSuccess(false);
    setError('');
    setDateError('');
  };

  // Helper function to get min/max dates and validate screening date
  const getDateConstraints = (content) => {
    // Calculate minimum date: today + 2 days
    const minDateBase = new Date();
    minDateBase.setDate(minDateBase.getDate() + 2);
    const defaultMinDate = minDateBase.toISOString().split('T')[0];
    
    if (!content) return { minDate: defaultMinDate, maxDate: undefined };
    
    let minDate = defaultMinDate; // Default: today + 2 days
    let maxDate = undefined;
    
    // For ENCORE content: restrict to date range
    if (content.is_encore && content.encore_start_date && content.encore_end_date) {
      // Use encore_start_date if it's after our min 2-day restriction
      minDate = content.encore_start_date > defaultMinDate ? content.encore_start_date : defaultMinDate;
      maxDate = content.encore_end_date;
    }
    // For coming_soon content: restrict to premiere date onwards
    else if (content.status === 'coming_soon' && content.premiere_date) {
      // Use premiere_date if it's after our min 2-day restriction
      minDate = content.premiere_date > defaultMinDate ? content.premiere_date : defaultMinDate;
    }
    
    return { minDate, maxDate };
  };

  // Validate a date against content constraints
  const validateScreeningDate = (date, content) => {
    if (!date || !content) return '';
    
    const { minDate, maxDate } = getDateConstraints(content);
    
    if (date < minDate) {
      if (content.is_encore && content.encore_start_date && content.encore_start_date > minDate) {
        return `La fecha debe ser a partir del ${new Date(content.encore_start_date + 'T00:00:00').toLocaleDateString('es-ES')}`;
      } else if (content.status === 'coming_soon' && content.premiere_date && content.premiere_date > minDate) {
        return `La fecha de proyección debe ser a partir del ${new Date(content.premiere_date + 'T00:00:00').toLocaleDateString('es-ES')}`;
      }
      return 'La fecha de proyección debe ser al menos 2 días a partir de hoy';
    }
    
    if (maxDate && date > maxDate) {
      return `La fecha debe ser antes del ${new Date(content.encore_end_date + 'T00:00:00').toLocaleDateString('es-ES')}`;
    }
    
    return ''; // No error
  };

  // Handle screening date change with validation
  const handleScreeningDateChange = (newDate) => {
    const validationError = validateScreeningDate(newDate, selectedContent);
    setDateError(validationError);
    setScreeningDate(newDate);
  };

  // Calendar export functions
  const generateCalendarEvent = (screening, content, venueName) => {
    const title = content?.title || 'Proyección Bravo';
    const description = `Proyección de ${title}${content?.composer ? ` - ${content.composer}` : ''}\\n\\nContenido: ${content?.category || 'N/A'}\\n\\nRecuerda revisar el manual del Bravo si tienes dudas: https://www.mediafire.com/file_premium/ggjv0rb8bl8rx7z/bravo_cinemastreaming.pdf/file`;
    const location = venueName || 'Sala de proyecciones';
    
    // Parse date and time
    const dateStr = screening.date?.split('T')[0] || screening.date;
    const timeStr = screening.time || '20:00';
    const [hours, minutes] = timeStr.split(':');
    
    const startDate = new Date(dateStr + 'T' + timeStr);
    const endDate = new Date(startDate.getTime() + 3 * 60 * 60 * 1000); // +3 hours
    
    return { title, description, location, startDate, endDate };
  };

  const formatDateForGoogle = (date) => {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  };

  const getGoogleCalendarUrl = (screening, content) => {
    const event = generateCalendarEvent(screening, content, profileData?.venue_name);
    const startStr = formatDateForGoogle(event.startDate);
    const endStr = formatDateForGoogle(event.endDate);
    
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title,
      dates: `${startStr}/${endStr}`,
      details: event.description.replace(/\\n/g, '\n'),
      location: event.location,
      sf: 'true'
    });
    
    return `https://www.google.com/calendar/render?${params.toString()}`;
  };

  const getOutlookCalendarUrl = (screening, content) => {
    const event = generateCalendarEvent(screening, content, profileData?.venue_name);
    
    const params = new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      startdt: event.startDate.toISOString(),
      enddt: event.endDate.toISOString(),
      subject: event.title,
      body: event.description.replace(/\\n/g, '\n'),
      location: event.location
    });
    
    return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
  };

  const downloadICSFile = (screening, content) => {
    const event = generateCalendarEvent(screening, content, profileData?.venue_name);
    
    const formatICSDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    };
    
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Bravo Cinemastreaming//ES
BEGIN:VEVENT
DTSTART:${formatICSDate(event.startDate)}
DTEND:${formatICSDate(event.endDate)}
SUMMARY:${event.title}
DESCRIPTION:${event.description.replace(/\\n/g, '\\n')}
LOCATION:${event.location}
END:VEVENT
END:VCALENDAR`;
    
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${event.title.replace(/[^a-zA-Z0-9]/g, '_')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // State for calendar dropdown
  const [openCalendarDropdown, setOpenCalendarDropdown] = useState(null);

  if (!cinema) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><p className="text-white">Cargando...</p></div>;

  const statusColors = { 
    pending: 'bg-yellow-500/20 text-yellow-400', 
    approved: 'bg-green-500/20 text-green-400', 
    rejected: 'bg-red-500/20 text-red-400', 
    completed: 'bg-blue-500/20 text-blue-400',
    modification_pending: 'bg-orange-500/20 text-orange-400',
    cancellation_pending: 'bg-red-500/20 text-red-300'
  };
  const statusLabels = { 
    pending: 'Pendiente', 
    approved: 'Aprobado', 
    rejected: 'Rechazado', 
    completed: 'Completado',
    modification_pending: 'Modificación pendiente',
    cancellation_pending: 'Cancelación pendiente'
  };

  // Helper function to check if a content's date has passed
  const isContentPast = (content) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (content.is_live && content.live_date) {
      const liveDate = new Date(content.live_date);
      liveDate.setHours(0, 0, 0, 0);
      return liveDate < today;
    }
    
    if (content.is_encore && content.encore_end_date) {
      const encoreEnd = new Date(content.encore_end_date);
      encoreEnd.setHours(0, 0, 0, 0);
      return encoreEnd < today;
    }
    
    return false;
  };

  // Filter content by category - use backend contents, exclude past directos/encores
  const filteredContent = allContents.filter(c => {
    // Exclude past directos and encores
    if (isContentPast(c)) return false;
    // Category filter
    return categoryFilter === 'all' || c.category === categoryFilter;
  });

  // Filter bookings by selected venue if multi-venue account
  const filterByVenue = (bookings) => {
    if (!profileData?.venues || profileData.venues.length <= 1) {
      return bookings; // No filtering needed for single venue
    }
    const selectedVenue = getSelectedVenue();
    return bookings.filter(b => {
      // If booking has venue_name, match it
      if (b.venue_name) {
        return b.venue_name === selectedVenue?.venue_name;
      }
      // If booking has venue_index, match it
      if (b.venue_index !== undefined && b.venue_index !== null) {
        return b.venue_index === selectedVenueIndex;
      }
      // Legacy bookings without venue info - show for all venues
      return true;
    });
  };

  // Separate bookings into pending and historical, filtered by venue
  const allPendingBookings = myBookings.filter(b => ['pending', 'approved', 'modification_pending', 'cancellation_pending'].includes(b.status));
  const allHistoricalBookings = myBookings.filter(b => ['completed', 'rejected', 'cancelled'].includes(b.status));
  const pendingBookings = filterByVenue(allPendingBookings);
  const historicalBookings = filterByVenue(allHistoricalBookings);

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-8 px-4">
      {/* Toast Notification for New Offers */}
      {showOfferToast && (
        <div 
          className="fixed top-4 right-4 z-50 animate-slide-in-right"
          style={{
            animation: 'slideInRight 0.5s ease-out, fadeOut 0.5s ease-in 5.5s forwards'
          }}
        >
          <div 
            className="bg-gradient-to-r from-green-600 to-emerald-500 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => { setShowOfferToast(false); setActiveTab('pending'); }}
            data-testid="offer-toast"
          >
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <span className="text-2xl">🎁</span>
            </div>
            <div>
              <p className="font-bold text-lg">{toastMessage}</p>
              <p className="text-white/80 text-sm">Click para ver la oferta →</p>
            </div>
            <button 
              onClick={(e) => { e.stopPropagation(); setShowOfferToast(false); }}
              className="ml-2 p-1 hover:bg-white/20 rounded"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Toast Notification for Change Requests */}
      {changeRequestSuccess && (
        <div 
          className="fixed top-4 right-4 z-50"
          style={{
            animation: 'slideInRight 0.3s ease-out'
          }}
        >
          <div 
            className={`${
              changeRequestSuccess.type === 'success' 
                ? 'bg-gradient-to-r from-green-600 to-emerald-500' 
                : 'bg-gradient-to-r from-red-600 to-rose-500'
            } text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 max-w-md`}
            data-testid="change-request-toast"
          >
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              {changeRequestSuccess.type === 'success' ? (
                <Check className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
            </div>
            <div className="flex-1">
              <p className="font-semibold">
                {changeRequestSuccess.type === 'success' ? 'Solicitud enviada' : 'Error'}
              </p>
              <p className="text-white/90 text-sm">{changeRequestSuccess.message}</p>
            </div>
            <button 
              onClick={() => setChangeRequestSuccess(null)}
              className="p-1 hover:bg-white/20 rounded flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Fixed Banner for Pending Offers */}
      {hasUnseenOffers && !showOfferToast && (
        <div 
          className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white py-3 px-4 shadow-lg cursor-pointer"
          onClick={() => setActiveTab('pending')}
          data-testid="offer-banner"
        >
          <div className="max-w-6xl mx-auto flex items-center justify-center gap-3">
            <span className="animate-bounce">🔔</span>
            <span className="font-medium">
              Tienes {newOffersCount} oferta{newOffersCount > 1 ? 's' : ''} esperando tu respuesta
            </span>
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold hover:bg-white/30 transition">
              Ver ofertas →
            </span>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes pulse-green {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7); }
          50% { box-shadow: 0 0 0 10px rgba(34, 197, 94, 0); }
        }
        .animate-pulse-green {
          animation: pulse-green 2s infinite;
        }
      `}</style>

      <div className={`max-w-6xl mx-auto ${hasUnseenOffers && !showOfferToast ? 'pt-12' : ''}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/"><img src={LOGO_URL} alt="Bravo Cinemastreaming" className="h-10 w-auto" /></Link>
          <div className="flex items-center gap-4">
            {/* Company Name and Venue Selector */}
            <div className="text-right hidden md:block">
              <p className="text-white/50 text-sm">{profileData?.company_name || cinema?.company_name || 'Portal de Cines'}</p>
              
              {/* Venue Selector - always show to allow adding venues */}
              <div className="relative">
                <button
                  onClick={() => setShowVenueSelector(!showVenueSelector)}
                  className="flex items-center gap-2 text-white font-medium hover:text-[#e4002b] transition-colors"
                  data-testid="venue-selector-btn"
                >
                  <Building2 className="w-4 h-4" />
                  <span>{getSelectedVenue()?.venue_name || 'Seleccionar recinto'}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showVenueSelector ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown */}
                {showVenueSelector && (
                  <div className="absolute right-0 top-full mt-2 w-72 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl z-50">
                    <div className="p-2">
                      {profileData?.venues && profileData.venues.length > 0 && (
                        <>
                          <p className="text-white/40 text-xs px-3 py-1 uppercase tracking-wider">Mis recintos</p>
                          {profileData.venues.map((venue, index) => (
                            <button
                              key={index}
                              onClick={() => handleVenueChange(index)}
                              className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                                selectedVenueIndex === index 
                                  ? 'bg-[#e4002b] text-white' 
                                  : 'text-white/70 hover:bg-white/10'
                              }`}
                              data-testid={`venue-option-${index}`}
                            >
                              <MapPin className="w-4 h-4 flex-shrink-0" />
                              <div className="overflow-hidden flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium truncate">{venue.venue_name || `Recinto ${index + 1}`}</p>
                                  {venue.status === 'pending' && (
                                    <span className="text-[10px] bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded">Pendiente</span>
                                  )}
                                </div>
                                {venue.address?.city && (
                                  <p className="text-xs opacity-70 truncate">{venue.address.city}</p>
                                )}
                              </div>
                              {selectedVenueIndex === index && venue.status === 'approved' && (
                                <Check className="w-4 h-4 ml-auto flex-shrink-0" />
                              )}
                            </button>
                          ))}
                          <div className="border-t border-white/10 my-2"></div>
                        </>
                      )}
                      {/* Add new venue button */}
                      <button
                        onClick={() => {
                          setShowVenueSelector(false);
                          setShowAddVenueModal(true);
                        }}
                        className="w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 text-[#e4002b] hover:bg-[#e4002b]/10 transition-colors"
                        data-testid="add-venue-btn"
                      >
                        <Plus className="w-4 h-4" />
                        <span className="font-medium">Añadir recinto</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Push Notification Toggle */}
            {pushSupported && (
              <button
                onClick={requestPushPermission}
                className={`p-2 rounded-lg transition-colors ${
                  pushSubscribed 
                    ? 'text-green-400 bg-green-500/10' 
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
                title={pushSubscribed ? 'Notificaciones activadas' : 'Activar notificaciones'}
                data-testid="push-toggle-btn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  {pushSubscribed && <circle cx="18" cy="5" r="3" fill="currentColor" />}
                </svg>
              </button>
            )}
            
            <button onClick={() => { localStorage.removeItem('cinema_session'); navigate('/cinema/login'); }} className="p-2 text-white/60 hover:text-white">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap">
          <button 
            onClick={() => { setActiveTab('new'); reset(); }} 
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${activeTab === 'new' ? 'bg-[#e4002b] text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
          >
            <Plus className="w-4 h-4" />Nueva solicitud
          </button>
          <button 
            onClick={() => setActiveTab('pending')} 
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium relative ${activeTab === 'pending' ? 'bg-[#e4002b] text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'} ${hasUnseenOffers ? 'animate-pulse-green' : ''}`}
            data-testid="pending-tab"
          >
            <Calendar className="w-4 h-4" />Mis solicitudes
            {(pendingBookings.length > 0 || newOffersCount > 0) && (
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${hasUnseenOffers ? 'bg-green-500 text-white animate-pulse' : 'bg-white/20'}`}>
                {pendingBookings.length + newOffersCount}
              </span>
            )}
            {hasUnseenOffers && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-ping" />
            )}
          </button>
          <button 
            onClick={() => setActiveTab('history')} 
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${activeTab === 'history' ? 'bg-[#e4002b] text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
          >
            <History className="w-4 h-4" />Histórico
          </button>
          <button 
            onClick={() => setActiveTab('calendar')} 
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${activeTab === 'calendar' ? 'bg-[#e4002b] text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
          >
            <Calendar className="w-4 h-4" />Calendario
          </button>
          <button 
            onClick={() => setActiveTab('account')} 
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${activeTab === 'account' ? 'bg-[#e4002b] text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
          >
            <User className="w-4 h-4" />Cuenta
          </button>
          <button 
            onClick={() => setActiveTab('reports')} 
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium relative ${activeTab === 'reports' ? 'bg-[#e4002b] text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
            data-testid="reports-tab"
          >
            <FileText className="w-4 h-4" />Reportes Taquilla
            {pendingReportsCount > 0 && (
              <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-bold bg-orange-500 text-white">
                {pendingReportsCount}
              </span>
            )}
          </button>
        </div>

        {/* New Booking Tab */}
        {activeTab === 'new' && (
          <div className="bg-[#141414] rounded-xl p-6">
            {success ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-green-500" />
                </div>
                <h2 className="text-white text-2xl font-bold mb-2">¡Solicitud enviada!</h2>
                <p className="text-white/60 mb-6">Tu solicitud de contratación ha sido recibida. Te contactaremos pronto.</p>
                <button onClick={reset} className="px-6 py-3 bg-[#e4002b] text-white font-medium rounded">Nueva solicitud</button>
              </div>
            ) : (
              <>
                <h2 className="text-white text-xl font-bold mb-6">
                  {step === 1 ? 'Selecciona contenido para contratar' : 'Datos de proyección'}
                </h2>
                
                {error && (
                  <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>
                )}
                
                {/* Step 1: Select Content */}
                {step === 1 && (
                  <>
                    {/* Category Filter */}
                    <div className="mb-6">
                      <div className="flex flex-wrap gap-2">
                        {CATEGORIES.map(cat => (
                          <button
                            key={cat.value}
                            onClick={() => setCategoryFilter(cat.value)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              categoryFilter === cat.value 
                                ? 'bg-[#e4002b] text-white' 
                                : 'bg-white/5 text-white/60 hover:bg-white/10'
                            }`}
                          >
                            {cat.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {loadingContents ? (
                      <div className="text-center py-12">
                        <p className="text-white/50">Cargando contenidos...</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {filteredContent.map(c => (
                          <div 
                            key={c.id} 
                            onClick={() => { setSelectedContent(c); setStep(2); }} 
                            className="rounded-lg overflow-hidden cursor-pointer group hover:ring-2 hover:ring-[#e4002b]/50"
                          >
                            <div className="aspect-[3/4] relative">
                              <img src={c.hero_image || c.image || c.heroImage || 'https://images.unsplash.com/photo-1507838153414-b4b713384a76?w=600&q=80'} alt={c.title} className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                              
                              {/* Live indicator */}
                              {c.is_live && (
                                <div className="absolute top-2 left-2">
                                  <span className="flex items-center gap-1 px-2 py-1 bg-[#e4002b] text-white text-xs rounded font-bold">
                                    <Radio className="w-3 h-3 animate-pulse" /> DIRECTO
                                  </span>
                                </div>
                              )}
                              
                              {/* Encore indicator */}
                              {c.is_encore && !c.is_live && (
                                <div className="absolute top-2 left-2">
                                  <span className="px-2 py-1 bg-amber-500 text-black text-xs rounded font-bold">
                                    ENCORE
                                  </span>
                                </div>
                              )}
                              
                              {/* Coming soon indicator */}
                              {c.status === 'coming_soon' && !c.is_live && !c.is_encore && (
                                <div className="absolute top-2 left-2">
                                  <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded font-bold">
                                    PRÓXIMAMENTE
                                  </span>
                                </div>
                              )}
                              
                              <div className="absolute top-2 right-2">
                                <span className="px-2 py-1 bg-black/50 text-white/80 text-xs rounded">{getCategoryLabel(c.category)}</span>
                              </div>
                              <div className="absolute bottom-0 p-4">
                                <h3 className="text-white font-semibold group-hover:text-[#e4002b]">{c.title}</h3>
                                <p className="text-[#e4002b] text-sm">{c.composer}</p>
                                {/* Show live date/time in grid */}
                                {c.is_live && c.live_date && (
                                  <p className="text-white/70 text-xs mt-1">
                                    {c.live_time}h · {new Date(c.live_date).toLocaleDateString('es-ES')}
                                  </p>
                                )}
                                {/* Show encore date range in grid */}
                                {c.is_encore && c.encore_start_date && c.encore_end_date && (
                                  <p className="text-amber-400 text-xs mt-1">
                                    Disponible: {new Date(c.encore_start_date).toLocaleDateString('es-ES')} - {new Date(c.encore_end_date).toLocaleDateString('es-ES')}
                                  </p>
                                )}
                                {/* Show coming soon availability date */}
                                {c.status === 'coming_soon' && c.premiere_date && !c.is_live && !c.is_encore && (
                                  <p className="text-blue-400 text-xs mt-1">
                                    Disponible desde: {new Date(c.premiere_date).toLocaleDateString('es-ES')}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {filteredContent.length === 0 && (
                      <div className="text-center py-12">
                        <Film className="w-12 h-12 text-white/20 mx-auto mb-4" />
                        <p className="text-white/50">No hay contenido disponible en esta categoría</p>
                      </div>
                    )}
                  </>
                )}

                {/* Step 2: Booking Details */}
                {step === 2 && selectedContent && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left: Content Info */}
                    <div className="bg-white/5 rounded-xl overflow-hidden">
                      {/* Live badge if applicable */}
                      {selectedContent.is_live && (
                        <div className="bg-[#e4002b] px-4 py-2 flex items-center gap-2">
                          <Radio className="w-4 h-4 text-white animate-pulse" />
                          <span className="text-white font-bold text-sm">EVENTO EN DIRECTO</span>
                        </div>
                      )}
                      {selectedContent.is_encore && !selectedContent.is_live && (
                        <div className="bg-amber-500 px-4 py-2 flex items-center gap-2">
                          <span className="text-black font-bold text-sm">ENCORE</span>
                        </div>
                      )}
                      {selectedContent.status === 'coming_soon' && !selectedContent.is_live && !selectedContent.is_encore && (
                        <div className="bg-blue-500 px-4 py-2 flex items-center gap-2">
                          <span className="text-white font-bold text-sm">PRÓXIMAMENTE</span>
                        </div>
                      )}
                      <img src={selectedContent.hero_image || selectedContent.image || selectedContent.heroImage || 'https://images.unsplash.com/photo-1507838153414-b4b713384a76?w=600&q=80'} alt="" className="w-full h-64 object-cover" />
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-white text-xl font-bold">{selectedContent.title}</h3>
                            <p className="text-[#e4002b]">{selectedContent.composer}</p>
                          </div>
                          <span className="px-3 py-1 bg-white/10 text-white/80 text-sm rounded">{getCategoryLabel(selectedContent.category)}</span>
                        </div>
                        
                        {/* Show live date/time info */}
                        {selectedContent.is_live && selectedContent.live_date && (
                          <div className="mb-4 p-3 bg-[#e4002b]/20 border border-[#e4002b]/50 rounded-lg">
                            <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Fecha y hora del directo</p>
                            <p className="text-white text-lg font-bold">
                              {selectedContent.live_time && `${selectedContent.live_time}h - `}
                              {new Date(selectedContent.live_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </p>
                          </div>
                        )}
                        
                        {/* Show encore date range info */}
                        {selectedContent.is_encore && selectedContent.encore_start_date && selectedContent.encore_end_date && (
                          <div className="mb-4 p-3 bg-amber-500/20 border border-amber-500/50 rounded-lg">
                            <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Período disponible para contratación</p>
                            <p className="text-amber-400 text-lg font-bold">
                              {new Date(selectedContent.encore_start_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })} - {new Date(selectedContent.encore_end_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </p>
                          </div>
                        )}
                        
                        {/* Show coming soon availability info */}
                        {selectedContent.status === 'coming_soon' && selectedContent.premiere_date && !selectedContent.is_live && !selectedContent.is_encore && (
                          <div className="mb-4 p-3 bg-blue-500/20 border border-blue-500/50 rounded-lg">
                            <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Disponible para contratación desde</p>
                            <p className="text-blue-400 text-lg font-bold">
                              {new Date(selectedContent.premiere_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </p>
                          </div>
                        )}
                        
                        <p className="text-white/60 text-sm mb-4 line-clamp-4">{selectedContent.synopsis}</p>
                        
                        {selectedContent.cast?.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-white/80 text-sm font-medium mb-2">Reparto principal</h4>
                            <div className="text-white/50 text-sm">
                              {selectedContent.cast.slice(0, 3).map((m, i) => (
                                <span key={i}>{m.performer}{i < 2 && selectedContent.cast.length > i + 1 ? ', ' : ''}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {selectedContent.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {selectedContent.tags.slice(0, 4).map((tag, i) => (
                              <span key={i} className="px-2 py-1 bg-white/10 text-white/60 text-xs rounded">{tag}</span>
                            ))}
                          </div>
                        )}

                        <button onClick={() => setStep(1)} className="mt-4 text-[#e4002b] text-sm hover:underline">
                          ← Cambiar contenido
                        </button>
                      </div>
                    </div>

                    {/* Right: Booking Form */}
                    <div className="space-y-6">
                      {/* Live Event Notice */}
                      {selectedContent.is_live && (
                        <div className="p-4 bg-[#e4002b]/10 border border-[#e4002b]/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Radio className="w-5 h-5 text-[#e4002b]" />
                            <span className="text-white font-medium">Evento en directo</span>
                          </div>
                          <p className="text-white/70 text-sm">
                            La fecha y hora de este contenido están fijadas por el evento en directo y no se pueden modificar.
                          </p>
                        </div>
                      )}

                      {/* Date Selection */}
                      <div>
                        <label className="block text-white font-medium mb-2">Fecha de proyección</label>
                        {selectedContent.is_live && selectedContent.live_date ? (
                          <div className="w-full px-4 py-3 bg-white/5 border border-[#e4002b]/50 rounded-lg text-white">
                            <span className="text-[#e4002b]">Fijada: </span>
                            {new Date(selectedContent.live_date).toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                          </div>
                        ) : (
                          <>
                            {/* Show encore date restriction message */}
                            {selectedContent.is_encore && selectedContent.encore_start_date && selectedContent.encore_end_date && (
                              <div className="mb-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                                <p className="text-amber-400 text-sm">
                                  <span className="font-bold">ENCORE:</span> Solo puedes contratar entre {new Date(selectedContent.encore_start_date + 'T00:00:00').toLocaleDateString('es-ES')} y {new Date(selectedContent.encore_end_date + 'T00:00:00').toLocaleDateString('es-ES')}
                                </p>
                              </div>
                            )}
                            {/* Show coming soon date restriction message */}
                            {selectedContent.status === 'coming_soon' && selectedContent.premiere_date && !selectedContent.is_encore && (
                              <div className="mb-2 px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                                <p className="text-blue-400 text-sm">
                                  <span className="font-bold">PRÓXIMAMENTE:</span> La fecha de proyección debe ser a partir del {new Date(selectedContent.premiere_date + 'T00:00:00').toLocaleDateString('es-ES')}
                                </p>
                              </div>
                            )}
                            <input 
                              type="date" 
                              value={screeningDate} 
                              onChange={e => handleScreeningDateChange(e.target.value)} 
                              min={getDateConstraints(selectedContent).minDate}
                              max={getDateConstraints(selectedContent).maxDate}
                              className={`w-full px-4 py-3 bg-white/5 border rounded-lg text-white focus:outline-none ${
                                dateError ? 'border-red-500 focus:border-red-500' : 'border-white/10 focus:border-[#e4002b]'
                              }`}
                            />
                            {/* Show date validation error */}
                            {dateError && (
                              <p className="mt-2 text-red-400 text-sm">{dateError}</p>
                            )}
                          </>
                        )}
                      </div>

                      {/* Time Slots */}
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="text-white font-medium">Horarios</label>
                          {!selectedContent.is_live && (
                            <button onClick={addTime} className="text-[#e4002b] text-sm flex items-center gap-1">
                              <Plus className="w-4 h-4" /> Añadir horario
                            </button>
                          )}
                        </div>
                        
                        {selectedContent.is_live && selectedContent.live_time ? (
                          <div className="w-full px-4 py-3 bg-white/5 border border-[#e4002b]/50 rounded-lg text-white flex items-center gap-2">
                            <Clock className="w-4 h-4 text-[#e4002b]" />
                            <span><span className="text-[#e4002b]">Fijada: </span>{selectedContent.live_time}h</span>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {screeningTimes.map((time, i) => (
                              <div key={i} className="flex gap-2">
                                <div className="flex-1 relative">
                                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                  <input 
                                    type="time" 
                                    value={time} 
                                    onChange={e => updateTime(i, e.target.value)} 
                                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none" 
                                  />
                                </div>
                                {screeningTimes.length > 1 && (
                                  <button onClick={() => removeTime(i)} className="p-3 text-red-400 hover:bg-red-500/10 rounded-lg">
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Marketing Support */}
                      <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg">
                        <input 
                          type="checkbox" 
                          id="marketing"
                          checked={marketingSupport} 
                          onChange={e => setMarketingSupport(e.target.checked)} 
                          className="w-5 h-5 rounded" 
                        />
                        <label htmlFor="marketing" className="text-white/80 cursor-pointer">
                          Necesito soporte de marketing
                        </label>
                      </div>

                      {/* Special Conditions Notice - Show when content has special conditions */}
                      {selectedContent.has_special_conditions ? (
                        <div className="space-y-4">
                          {/* Check if there's already an inquiry for this content */}
                          {(() => {
                            const existingInquiry = myInquiries.find(
                              i => i.content_id === selectedContent.id && ['pending', 'responded'].includes(i.status)
                            );
                            
                            if (inquirySuccess) {
                              return (
                                <div className="p-6 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                                  <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
                                  <p className="text-white font-medium mb-1">¡Consulta enviada!</p>
                                  <p className="text-white/60 text-sm">Recibirás una respuesta con las condiciones especiales.</p>
                                </div>
                              );
                            }
                            
                            if (existingInquiry && existingInquiry.status === 'pending') {
                              return (
                                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                                  <div className="flex items-center gap-2 mb-2">
                                    <AlertCircle className="w-5 h-5 text-yellow-400" />
                                    <span className="text-yellow-400 font-medium">Consulta pendiente</span>
                                  </div>
                                  <p className="text-white/60 text-sm">
                                    Ya has solicitado las condiciones para este contenido. Recibirás una respuesta pronto.
                                  </p>
                                </div>
                              );
                            }
                            
                            if (existingInquiry && existingInquiry.status === 'responded') {
                              return (
                                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Check className="w-5 h-5 text-green-400" />
                                    <span className="text-green-400 font-medium">Oferta recibida</span>
                                  </div>
                                  <div className="bg-white/5 rounded-lg p-3 mb-3">
                                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Condiciones especiales</p>
                                    <p className="text-white font-bold text-lg">
                                      {existingInquiry.pricing_type === 'tanto_alzado' 
                                        ? <span className="text-[#e4002b]">{existingInquiry.flat_fee_price}€ + IVA</span>
                                        : <span className="text-blue-400">{existingInquiry.exhibitor_percentage}% para el exhibidor</span>
                                      }
                                    </p>
                                    {existingInquiry.admin_response && (
                                      <p className="text-white/60 text-sm mt-2">{existingInquiry.admin_response}</p>
                                    )}
                                  </div>
                                  <p className="text-white/50 text-sm">
                                    Acepta la oferta en la pestaña "Mis solicitudes" para proceder con la contratación.
                                  </p>
                                </div>
                              );
                            }
                            
                            // No inquiry yet - show button to create one
                            return (
                              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                                <div className="flex items-center gap-2 mb-3">
                                  <AlertCircle className="w-5 h-5 text-amber-400" />
                                  <span className="text-amber-400 font-medium">Condiciones especiales</span>
                                </div>
                                <p className="text-white/60 text-sm mb-4">
                                  Este contenido requiere condiciones económicas especiales. Solicita una oferta personalizada para tu cine.
                                </p>
                                
                                {error && (
                                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-red-400 text-sm">
                                    {error}
                                  </div>
                                )}
                                
                                <button
                                  onClick={() => createInquiry(selectedContent.id)}
                                  disabled={sendingInquiry}
                                  className="w-full py-3 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50"
                                  data-testid="consult-conditions-btn"
                                >
                                  {sendingInquiry ? 'Enviando...' : 'Consultar condiciones'}
                                </button>
                              </div>
                            );
                          })()}
                          
                          {/* Back button */}
                          <button 
                            onClick={() => setStep(1)} 
                            className="w-full py-3 bg-white/10 text-white rounded-lg hover:bg-white/20"
                          >
                            ← Volver al catálogo
                          </button>
                        </div>
                      ) : (
                        <>
                          {/* Pricing Info - Standard pricing */}
                          {contentPrice && contentPrice.price_label && (
                            <div className="p-4 bg-gradient-to-r from-[#e4002b]/10 to-amber-500/10 border border-white/10 rounded-lg">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Tarifa aplicable</p>
                                  <p className="text-white font-bold text-lg">
                                    {contentPrice.pricing_type === 'tanto_alzado' ? (
                                      <span className="text-[#e4002b]">{contentPrice.price_label}</span>
                                    ) : (
                                      <span className="text-blue-400">{contentPrice.price_label}</span>
                                    )}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    contentPrice.pricing_type === 'tanto_alzado' 
                                      ? 'bg-[#e4002b]/20 text-[#e4002b]' 
                                      : 'bg-blue-500/20 text-blue-400'
                                  }`}>
                                    {contentPrice.pricing_type === 'tanto_alzado' ? 'Flat Fee' : 'Box Office Split'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                          {loadingPrice && (
                            <div className="p-4 bg-white/5 rounded-lg animate-pulse">
                              <div className="h-4 bg-white/10 rounded w-24 mb-2"></div>
                              <div className="h-6 bg-white/10 rounded w-32"></div>
                            </div>
                          )}
                          {contentPrice && !contentPrice.price_label && !loadingPrice && (
                            <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                              <p className="text-yellow-400 text-sm">
                                Contacta con el equipo comercial para conocer las condiciones económicas aplicables.
                              </p>
                            </div>
                          )}

                          {/* Submit */}
                          <div className="flex gap-4 pt-4 border-t border-white/10">
                            <button 
                              onClick={() => setStep(1)} 
                              className="px-6 py-3 bg-white/10 text-white rounded-lg hover:bg-white/20"
                            >
                              Atrás
                            </button>
                            <button 
                              onClick={handleSubmit} 
                              disabled={loading || dateError || (!selectedContent.is_live && (!screeningDate || !screeningTimes.some(t => t)))}
                              className="flex-1 px-6 py-3 bg-[#e4002b] text-white font-medium rounded-lg hover:bg-[#c70024] disabled:opacity-50 disabled:cursor-not-allowed"
                              data-testid="submit-booking-btn"
                            >
                              {loading ? 'Enviando...' : 'Solicitar contratación'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Pending Bookings Tab */}
        {activeTab === 'pending' && (
          <div className="space-y-6">
            {/* Inquiries with offers - Special Conditions */}
            {myInquiries.filter(i => ['pending', 'responded'].includes(i.status)).length > 0 && (
              <div className="bg-[#141414] rounded-xl p-6">
                <h2 className="text-white text-xl font-bold mb-6 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-400" />
                  Consultas de condiciones especiales
                </h2>
                <div className="space-y-4">
                  {myInquiries.filter(i => ['pending', 'responded'].includes(i.status)).map(inquiry => {
                    const c = allContents.find(x => x.id === inquiry.content_id);
                    
                    const inquiryStatusColors = {
                      pending: 'bg-yellow-500/20 text-yellow-400',
                      responded: 'bg-green-500/20 text-green-400',
                      accepted: 'bg-blue-500/20 text-blue-400',
                      rejected: 'bg-red-500/20 text-red-400'
                    };
                    const inquiryStatusLabels = {
                      pending: 'Pendiente de respuesta',
                      responded: 'Oferta recibida',
                      accepted: 'Aceptada - Reserva creada',
                      rejected: 'Rechazada'
                    };
                    
                    return (
                      <div key={inquiry.id} className="p-4 bg-white/5 rounded-lg border border-amber-500/20">
                        <div className="flex items-start gap-4">
                          {c && <img src={c.hero_image || c.image || c.heroImage} alt="" className="w-20 h-28 object-cover rounded" />}
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h3 className="text-white font-medium">{c?.title || 'Contenido'}</h3>
                                <p className="text-[#e4002b] text-sm">{c?.composer}</p>
                              </div>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${inquiryStatusColors[inquiry.status]}`}>
                                {inquiryStatusLabels[inquiry.status]}
                              </span>
                            </div>
                            
                            <p className="text-white/40 text-xs mb-3">
                              Solicitado: {new Date(inquiry.created_at).toLocaleDateString('es-ES')}
                            </p>
                            
                            {/* Show offer if responded */}
                            {inquiry.status === 'responded' && (
                              <div className="mt-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                                <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Oferta recibida</p>
                                <p className="text-white font-bold text-xl mb-2">
                                  {inquiry.pricing_type === 'tanto_alzado' 
                                    ? <span className="text-[#e4002b]">{inquiry.flat_fee_price}€ + IVA</span>
                                    : <span className="text-blue-400">{inquiry.exhibitor_percentage}% para el exhibidor</span>
                                  }
                                </p>
                                {inquiry.admin_response && (
                                  <p className="text-white/60 text-sm mb-4">{inquiry.admin_response}</p>
                                )}
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => acceptInquiry(inquiry.id)}
                                    className="flex-1 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600"
                                    data-testid="accept-offer-btn"
                                  >
                                    Aceptar oferta
                                  </button>
                                  <button
                                    onClick={() => rejectInquiry(inquiry.id)}
                                    className="flex-1 py-3 bg-red-500/20 text-red-400 font-semibold rounded-lg hover:bg-red-500/30 border border-red-500/30"
                                    data-testid="reject-offer-btn"
                                  >
                                    Rechazar oferta
                                  </button>
                                </div>
                                <p className="text-white/40 text-xs mt-2 text-center">
                                  Al aceptar, se creará una reserva pendiente de aprobación
                                </p>
                              </div>
                            )}
                            
                            {inquiry.status === 'pending' && (
                              <p className="text-white/50 text-sm">
                                Tu consulta está siendo revisada. Recibirás una oferta pronto.
                              </p>
                            )}
                            
                            {inquiry.status === 'accepted' && (
                              <div className="mt-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                                <p className="text-blue-400 font-medium text-sm">
                                  ✓ Oferta aceptada - Tu reserva ha sido enviada para aprobación del administrador
                                </p>
                                <p className="text-white/40 text-xs mt-1">
                                  Aceptada: {inquiry.accepted_at ? new Date(inquiry.accepted_at).toLocaleDateString('es-ES') : '-'}
                                </p>
                              </div>
                            )}
                            
                            {inquiry.status === 'rejected' && (
                              <div className="mt-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                                <p className="text-red-400 font-medium text-sm">
                                  ✗ Has rechazado esta oferta
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Regular Pending Bookings */}
            <div className="bg-[#141414] rounded-xl p-6">
              <h2 className="text-white text-xl font-bold mb-6">Mis solicitudes de contratación</h2>
              {pendingBookings.length === 0 ? (
                <div className="text-center py-12">
                  <Film className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">No tienes solicitudes de contratación pendientes</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingBookings.map(b => {
                    const c = allContents.find(x => x.id === b.content_id);
                    const canModify = canModifyBooking(b);
                    return (
                      <div key={b.id} className="flex items-start gap-4 p-4 bg-white/5 rounded-lg">
                        {c && <img src={c.hero_image || c.image || c.heroImage} alt="" className="w-20 h-28 object-cover rounded" />}
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-white font-medium">{c?.title || 'Contenido'}</h3>
                              <p className="text-[#e4002b] text-sm">{c?.composer}</p>
                              {c?.is_live && <span className="text-xs text-orange-400 mt-1 inline-block">En directo</span>}
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[b.status] || ''}`}>
                              {statusLabels[b.status] || b.status}
                            </span>
                          </div>
                          <div className="mt-2 text-white/50 text-sm">
                            {b.screenings?.map((s, i) => (
                              <span key={i} className="mr-3">
                                <Calendar className="w-3 h-3 inline mr-1" />{s.date} 
                                <Clock className="w-3 h-3 inline mx-1" />{s.time}
                              </span>
                            ))}
                          </div>
                          {/* Action buttons */}
                          {b.status === 'approved' && (
                            <div className="mt-3 flex gap-2">
                              {canModify && (
                                <button
                                  onClick={() => {
                                    setModifyData({ screenings: [...b.screenings], reason: '' });
                                    setShowModifyModal(b);
                                  }}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded text-sm transition-colors"
                                >
                                  <Edit className="w-3 h-3" /> Modificar
                                </button>
                              )}
                              <button
                                onClick={() => setShowCancelModal(b)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded text-sm transition-colors"
                              >
                                <X className="w-3 h-3" /> Cancelar
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Accepted Inquiries */}
            {myInquiries.filter(i => i.status === 'accepted').length > 0 && (
              <div className="bg-[#141414] rounded-xl p-6">
                <h2 className="text-white text-xl font-bold mb-6 flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-400" />
                  Ofertas aceptadas
                </h2>
                <div className="space-y-4">
                  {myInquiries.filter(i => i.status === 'accepted').map(inquiry => {
                    const c = allContents.find(x => x.id === inquiry.content_id);
                    return (
                      <div key={inquiry.id} className="flex items-start gap-4 p-4 bg-green-500/5 rounded-lg border border-green-500/20">
                        {c && <img src={c.hero_image || c.image || c.heroImage} alt="" className="w-20 h-28 object-cover rounded" />}
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-white font-medium">{c?.title || 'Contenido'}</h3>
                              <p className="text-[#e4002b] text-sm">{c?.composer}</p>
                            </div>
                            <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                              Aceptada
                            </span>
                          </div>
                          <div className="mt-2">
                            <p className="text-white/50 text-sm">
                              Condiciones: {inquiry.pricing_type === 'tanto_alzado' 
                                ? `${inquiry.flat_fee_price}€ + IVA`
                                : `${inquiry.exhibitor_percentage}% para el exhibidor`
                              }
                            </p>
                            <p className="text-white/40 text-xs mt-1">
                              Aceptada: {inquiry.accepted_at ? new Date(inquiry.accepted_at).toLocaleDateString('es-ES') : '-'}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Historical Bookings Tab */}
        {activeTab === 'history' && (
          <div className="bg-[#141414] rounded-xl p-6">
            <h2 className="text-white text-xl font-bold mb-6">Histórico de pases programados</h2>
            {historicalBookings.length === 0 ? (
              <div className="text-center py-12">
                <History className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">No hay pases en el histórico</p>
              </div>
            ) : (
              <div className="space-y-4">
                {historicalBookings.map(b => {
                  const c = allContents.find(x => x.id === b.content_id);
                  return (
                    <div key={b.id} className="flex items-start gap-4 p-4 bg-white/5 rounded-lg opacity-75">
                      {c && <img src={c.hero_image || c.image || c.heroImage} alt="" className="w-20 h-28 object-cover rounded grayscale" />}
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-white font-medium">{c?.title || 'Contenido'}</h3>
                            <p className="text-white/50 text-sm">{c?.composer}</p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[b.status] || ''}`}>
                            {statusLabels[b.status] || b.status}
                          </span>
                        </div>
                        <div className="mt-2 text-white/50 text-sm">
                          {b.screenings?.map((s, i) => (
                            <span key={i} className="mr-3">
                              <Calendar className="w-3 h-3 inline mr-1" />{s.date}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Calendar Tab - Programming Calendar */}
        {activeTab === 'calendar' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar - Left Side */}
            <div className="lg:col-span-2 bg-[#141414] rounded-xl p-6">
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1))}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-white" />
                </button>
                <h3 className="text-white text-xl font-bold">
                  {['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'][calendarDate.getMonth()]} {calendarDate.getFullYear()}
                </h3>
                <button
                  onClick={() => setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1))}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-white" />
                </button>
              </div>

              {/* Day Names */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(day => (
                  <div key={day} className="text-center text-white/50 text-sm py-3">{day}</div>
                ))}
              </div>

              {/* Calendar Days */}
              {(() => {
                const year = calendarDate.getFullYear();
                const month = calendarDate.getMonth();
                const firstDayOfWeek = new Date(year, month, 1).getDay();
                const emptyDays = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
                const daysInMonth = new Date(year, month + 1, 0).getDate();
                const todayStr = new Date().toDateString();
                
                return (
                  <div className="grid grid-cols-7 gap-2">
                    {/* Empty cells */}
                    {Array.from({ length: emptyDays }).map((_, i) => (
                      <div key={`empty-${i}`} className="aspect-square" />
                    ))}
                    
                    {/* Days */}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const day = i + 1;
                      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      
                      const dayBookings = (myBookings || []).filter(b => 
                        b.screenings?.some(s => s.date === dateStr) && 
                        ['approved', 'confirmed'].includes(b.status)
                      );
                      
                      const isToday = todayStr === new Date(year, month, day).toDateString();
                      const hasBookings = dayBookings.length > 0;
                      
                      // Determine booking type for color
                      let bookingType = null;
                      if (hasBookings) {
                        const booking = dayBookings[0];
                        const content = (allContents || []).find(c => c.id === booking.content_id);
                        if (content?.is_live) bookingType = 'live';
                        else if (content?.is_encore) bookingType = 'encore';
                        else bookingType = 'recorded';
                      }
                      
                      return (
                        <div
                          key={day}
                          className={`aspect-square rounded-lg flex flex-col items-center justify-center transition-colors ${
                            isToday ? 'bg-white/10 ring-2 ring-[#e4002b]' : 
                            hasBookings ? 'bg-white/5 hover:bg-white/10' : 'hover:bg-white/5'
                          }`}
                        >
                          <span className={`text-lg ${hasBookings ? 'text-white font-semibold' : 'text-white/60'}`}>{day}</span>
                          {hasBookings && (
                            <span className={`w-2 h-2 rounded-full mt-1 ${
                              bookingType === 'live' ? 'bg-[#e4002b]' :
                              bookingType === 'encore' ? 'bg-amber-500' : 'bg-white/50'
                            }`} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Legend */}
              <div className="flex items-center gap-6 mt-6 pt-4 border-t border-white/10">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-[#e4002b]" />
                  <span className="text-white/60 text-sm">Directo</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-amber-500" />
                  <span className="text-white/60 text-sm">Encore</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-white/50" />
                  <span className="text-white/60 text-sm">Grabado</span>
                </div>
              </div>
            </div>

            {/* Upcoming Screenings - Right Sidebar */}
            <div className="lg:col-span-1 bg-[#141414] rounded-xl p-6">
              <h3 className="text-white font-semibold mb-6 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#e4002b]" />
                Próximas Proyecciones
              </h3>
              
              {(() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                const upcomingScreenings = (myBookings || [])
                  .filter(b => ['approved', 'confirmed'].includes(b.status))
                  .flatMap(b => {
                    const content = (allContents || []).find(c => c.id === b.content_id);
                    return (b.screenings || []).map(s => ({
                      ...s,
                      booking: b,
                      content: content
                    }));
                  })
                  .filter(s => {
                    if (!s.date) return false;
                    const screeningDate = new Date(s.date);
                    return screeningDate >= today;
                  })
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .slice(0, 10);
                
                if (upcomingScreenings.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <Calendar className="w-10 h-10 text-white/20 mx-auto mb-3" />
                      <p className="text-white/50 text-sm">No tienes proyecciones programadas</p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    {upcomingScreenings.map((screening, idx) => {
                      const screeningDate = new Date(screening.date);
                      const monthNames = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
                      const category = screening.content?.category || '';
                      
                      // Determine type color
                      let typeColor = 'bg-white/50';
                      if (screening.content?.is_live) typeColor = 'bg-[#e4002b]';
                      else if (screening.content?.is_encore) typeColor = 'bg-amber-500';
                      
                      return (
                        <div key={idx} className="flex items-start gap-3 group">
                          {/* Date */}
                          <div className="flex-shrink-0 w-12 text-center">
                            <p className="text-[#e4002b] text-xs font-medium">{monthNames[screeningDate.getMonth()]}</p>
                            <p className="text-white text-2xl font-bold">{screeningDate.getDate()}</p>
                          </div>
                          
                          {/* Content info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className={`w-2 h-2 rounded-full ${typeColor}`} />
                              <span className="text-white/50 text-xs">{category}</span>
                              <span className="text-white/30 text-xs ml-auto">{screening.time}h</span>
                            </div>
                            <p className="text-white font-medium text-sm line-clamp-2">{screening.content?.title || 'Sin título'}</p>
                            <p className="text-white/50 text-xs mt-0.5">{screening.content?.composer || ''}</p>
                            
                            {/* Add to Calendar Button */}
                            <div className="relative mt-2">
                              <button
                                onClick={() => setOpenCalendarDropdown(openCalendarDropdown === idx ? null : idx)}
                                className="flex items-center gap-1 text-xs text-[#e4002b] hover:text-white transition-colors"
                              >
                                <Plus className="w-3 h-3" />
                                Añadir al calendario
                                <ChevronDown className={`w-3 h-3 transition-transform ${openCalendarDropdown === idx ? 'rotate-180' : ''}`} />
                              </button>
                              
                              {openCalendarDropdown === idx && (
                                <div className="mt-2 flex items-center gap-2">
                                  {/* Google Calendar */}
                                  <a
                                    href={getGoogleCalendarUrl(screening, screening.content)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center w-9 h-9 bg-white/10 hover:bg-[#4285F4]/20 rounded-lg transition-colors group"
                                    title="Google Calendar"
                                    onClick={() => setOpenCalendarDropdown(null)}
                                  >
                                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                    </svg>
                                  </a>
                                  
                                  {/* Outlook */}
                                  <a
                                    href={getOutlookCalendarUrl(screening, screening.content)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center w-9 h-9 bg-white/10 hover:bg-[#0078D4]/20 rounded-lg transition-colors group"
                                    title="Outlook"
                                    onClick={() => setOpenCalendarDropdown(null)}
                                  >
                                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                                      <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.152-.354.228-.587.228H8.616c-.233 0-.43-.076-.587-.228-.158-.152-.237-.345-.237-.576V7.387c0-.23.079-.424.237-.576.157-.152.354-.228.587-.228h14.559c.233 0 .429.076.587.228.159.152.238.345.238.576z"/>
                                      <path fill="#0078D4" d="M24 7.387L15.778 13.5 8.616 7.16h14.559c.233 0 .429.076.587.228z"/>
                                      <path fill="#0078D4" d="M9.941 5.75v13.5H1.5c-.398 0-.75-.352-.75-.75V6.5c0-.398.352-.75.75-.75h8.441z"/>
                                      <ellipse fill="white" cx="5.5" cy="12.5" rx="2.5" ry="3"/>
                                    </svg>
                                  </a>
                                  
                                  {/* Apple/ICS Download */}
                                  <button
                                    onClick={() => {
                                      downloadICSFile(screening, screening.content);
                                      setOpenCalendarDropdown(null);
                                    }}
                                    className="flex items-center justify-center w-9 h-9 bg-white/10 hover:bg-white/20 rounded-lg transition-colors group"
                                    title="Descargar .ics (Apple Calendar, etc.)"
                                  >
                                    <svg className="w-5 h-5 text-white/80 group-hover:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                                    </svg>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Account Tab */}
        {activeTab === 'account' && profileData && (
          <div className="space-y-6">
            {/* Bravo Number - Highlighted */}
            {profileData.bravo_number && (
              <div className="bg-gradient-to-r from-[#e4002b] to-[#c70024] rounded-xl p-6 text-center">
                <p className="text-white/80 text-sm uppercase tracking-wider mb-2">Tu número de Bravo</p>
                <p className="text-white text-5xl font-bold tracking-widest">{profileData.bravo_number}</p>
                {/* Bravo Rental Mode */}
                {cinemaPricing?.bravo_rental && (
                  <div className="mt-4 pt-4 border-t border-white/20">
                    <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Modalidad de Alquiler</p>
                    <p className="text-white font-medium">
                      {cinemaPricing.bravo_rental.mode === 'por_evento' && 'Por Evento (50€/proyección)'}
                      {cinemaPricing.bravo_rental.mode === 'anual_3_years' && `Anual Premium (3 años) - ${cinemaPricing.bravo_rental.payment_type === 'mensual' ? '15€/mes' : '95€ pago único'}`}
                      {cinemaPricing.bravo_rental.mode === 'anual_1_year' && `Anual Flexible (1 año) - ${cinemaPricing.bravo_rental.payment_type === 'mensual' ? '20€/mes' : '150€ pago único'}`}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Pending Changes Notice */}
            {profileData.pending_changes_status === 'pending' && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-medium">Cambios pendientes de validación</p>
                  <p className="text-white/60 text-sm">Has solicitado cambios en tu perfil. Un administrador los revisará pronto.</p>
                </div>
              </div>
            )}

            {profileSaveStatus === 'success' && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
                <Check className="w-5 h-5 text-green-500" />
                <p className="text-white">Cambios enviados para validación. Recibirás confirmación cuando sean aprobados.</p>
              </div>
            )}

            <div className="bg-[#141414] rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-white text-xl font-bold flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#e4002b]" />
                  Datos de mi cine
                </h2>
                {!editingProfile ? (
                  <button 
                    onClick={() => setEditingProfile(true)}
                    className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 text-sm"
                  >
                    Editar datos
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button 
                      onClick={() => { setEditingProfile(false); loadFullProfile(cinema.cinema_id); }}
                      className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 text-sm"
                    >
                      Cancelar
                    </button>
                    <button 
                      onClick={handleSaveProfile}
                      disabled={savingProfile}
                      className="px-4 py-2 bg-[#e4002b] text-white rounded-lg hover:bg-[#c70024] text-sm flex items-center gap-2 disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      {savingProfile ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                  </div>
                )}
              </div>

              {/* Company Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Nombre del recinto</label>
                  {editingProfile ? (
                    <input 
                      type="text" 
                      value={profileData.venue_name || ''} 
                      onChange={(e) => setProfileData({...profileData, venue_name: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                    />
                  ) : (
                    <p className="text-white font-medium text-lg">{profileData.venue_name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Empresa</label>
                  {editingProfile ? (
                    <input 
                      type="text" 
                      value={profileData.company_name || ''} 
                      onChange={(e) => setProfileData({...profileData, company_name: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                    />
                  ) : (
                    <p className="text-white">{profileData.company_name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">CIF</label>
                  {editingProfile ? (
                    <input 
                      type="text" 
                      value={profileData.cif || ''} 
                      onChange={(e) => setProfileData({...profileData, cif: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                    />
                  ) : (
                    <p className="text-white">{profileData.cif || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Estado</label>
                  <span className={`inline-flex px-3 py-1 rounded text-sm font-medium ${
                    profileData.status === 'approved' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                    profileData.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                    'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                    {profileData.status === 'approved' ? 'Aprobado' : profileData.status === 'pending' ? 'Pendiente' : 'Rechazado'}
                  </span>
                </div>
              </div>

              {/* Contact Info */}
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <User className="w-4 h-4 text-[#e4002b]" />
                Datos de contacto
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Nombre de contacto</label>
                  {editingProfile ? (
                    <input 
                      type="text" 
                      value={profileData.contact_name || ''} 
                      onChange={(e) => setProfileData({...profileData, contact_name: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                    />
                  ) : (
                    <p className="text-white">{profileData.contact_name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Email</label>
                  <p className="text-white flex items-center gap-2">
                    <Mail className="w-4 h-4 text-white/50" />
                    {profileData.contact_email}
                  </p>
                  <p className="text-white/40 text-xs mt-1">El email no se puede modificar</p>
                </div>
                <div>
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Teléfono</label>
                  {editingProfile ? (
                    <input 
                      type="tel" 
                      value={profileData.contact_phone || ''} 
                      onChange={(e) => setProfileData({...profileData, contact_phone: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                    />
                  ) : (
                    <p className="text-white flex items-center gap-2">
                      <Phone className="w-4 h-4 text-white/50" />
                      {profileData.contact_phone || '-'}
                    </p>
                  )}
                </div>
              </div>

              {/* Address */}
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#e4002b]" />
                Dirección
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="md:col-span-2">
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Calle</label>
                  {editingProfile ? (
                    <input 
                      type="text" 
                      value={profileData.address?.street || ''} 
                      onChange={(e) => setProfileData({...profileData, address: {...profileData.address, street: e.target.value}})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                    />
                  ) : (
                    <p className="text-white">{profileData.address?.street || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Ciudad</label>
                  {editingProfile ? (
                    <input 
                      type="text" 
                      value={profileData.address?.city || ''} 
                      onChange={(e) => setProfileData({...profileData, address: {...profileData.address, city: e.target.value}})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                    />
                  ) : (
                    <p className="text-white">{profileData.address?.city || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Código Postal</label>
                  {editingProfile ? (
                    <input 
                      type="text" 
                      value={profileData.address?.postal_code || ''} 
                      onChange={(e) => setProfileData({...profileData, address: {...profileData.address, postal_code: e.target.value}})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                    />
                  ) : (
                    <p className="text-white">{profileData.address?.postal_code || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Provincia</label>
                  {editingProfile ? (
                    <input 
                      type="text" 
                      value={profileData.address?.province || ''} 
                      onChange={(e) => setProfileData({...profileData, address: {...profileData.address, province: e.target.value}})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                    />
                  ) : (
                    <p className="text-white">{profileData.address?.province || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">País</label>
                  {editingProfile ? (
                    <input 
                      type="text" 
                      value={profileData.address?.country || ''} 
                      onChange={(e) => setProfileData({...profileData, address: {...profileData.address, country: e.target.value}})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                    />
                  ) : (
                    <p className="text-white">{profileData.address?.country || '-'}</p>
                  )}
                </div>
              </div>

              {/* Technical Specs */}
              {profileData.technical_specs && (
                <>
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Monitor className="w-4 h-4 text-[#e4002b]" />
                    Especificaciones técnicas
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Sistema de proyección</label>
                      {editingProfile ? (
                        <input 
                          type="text" 
                          value={profileData.technical_specs?.projection_system || ''} 
                          onChange={(e) => setProfileData({...profileData, technical_specs: {...profileData.technical_specs, projection_system: e.target.value}})}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                        />
                      ) : (
                        <p className="text-white">{profileData.technical_specs?.projection_system || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Sistema de sonido</label>
                      {editingProfile ? (
                        <input 
                          type="text" 
                          value={profileData.technical_specs?.sound_system || ''} 
                          onChange={(e) => setProfileData({...profileData, technical_specs: {...profileData.technical_specs, sound_system: e.target.value}})}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                        />
                      ) : (
                        <p className="text-white">{profileData.technical_specs?.sound_system || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Tamaño de pantalla</label>
                      {editingProfile ? (
                        <input 
                          type="text" 
                          value={profileData.technical_specs?.screen_size || ''} 
                          onChange={(e) => setProfileData({...profileData, technical_specs: {...profileData.technical_specs, screen_size: e.target.value}})}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                        />
                      ) : (
                        <p className="text-white">{profileData.technical_specs?.screen_size || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Aforo</label>
                      {editingProfile ? (
                        <input 
                          type="number" 
                          value={profileData.technical_specs?.seating_capacity || ''} 
                          onChange={(e) => setProfileData({...profileData, technical_specs: {...profileData.technical_specs, seating_capacity: parseInt(e.target.value) || 0}})}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                        />
                      ) : (
                        <p className="text-white">{profileData.technical_specs?.seating_capacity ? `${profileData.technical_specs.seating_capacity} butacas` : '-'}</p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Additional Contacts Section */}
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2 mt-8">
                <Users className="w-4 h-4 text-[#e4002b]" />
                Contactos adicionales
              </h3>
              
              {/* Administration Contact */}
              <div className="bg-white/5 rounded-lg p-4 mb-4">
                <p className="text-white/70 text-sm font-medium mb-3">Contacto Administración</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Nombre</label>
                    {editingProfile ? (
                      <input 
                        type="text" 
                        value={profileData.contact_admin?.name || ''} 
                        onChange={(e) => setProfileData({...profileData, contact_admin: {...(profileData.contact_admin || {}), name: e.target.value}})}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none text-sm"
                        placeholder="Nombre completo"
                      />
                    ) : (
                      <p className="text-white text-sm">{profileData.contact_admin?.name || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Email</label>
                    {editingProfile ? (
                      <input 
                        type="email" 
                        value={profileData.contact_admin?.email || ''} 
                        onChange={(e) => setProfileData({...profileData, contact_admin: {...(profileData.contact_admin || {}), email: e.target.value}})}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none text-sm"
                        placeholder="email@empresa.com"
                      />
                    ) : (
                      <p className="text-white text-sm">{profileData.contact_admin?.email || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Teléfono móvil</label>
                    {editingProfile ? (
                      <input 
                        type="tel" 
                        value={profileData.contact_admin?.phone || ''} 
                        onChange={(e) => setProfileData({...profileData, contact_admin: {...(profileData.contact_admin || {}), phone: e.target.value}})}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none text-sm"
                        placeholder="+34 600 000 000"
                      />
                    ) : (
                      <p className="text-white text-sm">{profileData.contact_admin?.phone || '-'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Technical Contact */}
              <div className="bg-white/5 rounded-lg p-4 mb-4">
                <p className="text-white/70 text-sm font-medium mb-3">Contacto Técnico</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Nombre</label>
                    {editingProfile ? (
                      <input 
                        type="text" 
                        value={profileData.contact_tech?.name || ''} 
                        onChange={(e) => setProfileData({...profileData, contact_tech: {...(profileData.contact_tech || {}), name: e.target.value}})}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none text-sm"
                        placeholder="Nombre completo"
                      />
                    ) : (
                      <p className="text-white text-sm">{profileData.contact_tech?.name || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Email</label>
                    {editingProfile ? (
                      <input 
                        type="email" 
                        value={profileData.contact_tech?.email || ''} 
                        onChange={(e) => setProfileData({...profileData, contact_tech: {...(profileData.contact_tech || {}), email: e.target.value}})}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none text-sm"
                        placeholder="email@empresa.com"
                      />
                    ) : (
                      <p className="text-white text-sm">{profileData.contact_tech?.email || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Teléfono móvil</label>
                    {editingProfile ? (
                      <input 
                        type="tel" 
                        value={profileData.contact_tech?.phone || ''} 
                        onChange={(e) => setProfileData({...profileData, contact_tech: {...(profileData.contact_tech || {}), phone: e.target.value}})}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none text-sm"
                        placeholder="+34 600 000 000"
                      />
                    ) : (
                      <p className="text-white text-sm">{profileData.contact_tech?.phone || '-'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Marketing Contact */}
              <div className="bg-white/5 rounded-lg p-4 mb-4">
                <p className="text-white/70 text-sm font-medium mb-3">Contacto Marketing</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Nombre</label>
                    {editingProfile ? (
                      <input 
                        type="text" 
                        value={profileData.contact_marketing?.name || ''} 
                        onChange={(e) => setProfileData({...profileData, contact_marketing: {...(profileData.contact_marketing || {}), name: e.target.value}})}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none text-sm"
                        placeholder="Nombre completo"
                      />
                    ) : (
                      <p className="text-white text-sm">{profileData.contact_marketing?.name || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Email</label>
                    {editingProfile ? (
                      <input 
                        type="email" 
                        value={profileData.contact_marketing?.email || ''} 
                        onChange={(e) => setProfileData({...profileData, contact_marketing: {...(profileData.contact_marketing || {}), email: e.target.value}})}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none text-sm"
                        placeholder="email@empresa.com"
                      />
                    ) : (
                      <p className="text-white text-sm">{profileData.contact_marketing?.email || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Teléfono móvil</label>
                    {editingProfile ? (
                      <input 
                        type="tel" 
                        value={profileData.contact_marketing?.phone || ''} 
                        onChange={(e) => setProfileData({...profileData, contact_marketing: {...(profileData.contact_marketing || {}), phone: e.target.value}})}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none text-sm"
                        placeholder="+34 600 000 000"
                      />
                    ) : (
                      <p className="text-white text-sm">{profileData.contact_marketing?.phone || '-'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Shipping Address for Promotional Materials */}
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2 mt-8">
                <Truck className="w-4 h-4 text-[#e4002b]" />
                Dirección de envío de material promocional
              </h3>
              <p className="text-white/50 text-sm mb-4">Dirección para el envío de carteles físicos y material promocional</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Dirección</label>
                  {editingProfile ? (
                    <input 
                      type="text" 
                      value={profileData.shipping_address?.street || ''} 
                      onChange={(e) => setProfileData({...profileData, shipping_address: {...(profileData.shipping_address || {}), street: e.target.value}})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                      placeholder="Calle, número, piso..."
                    />
                  ) : (
                    <p className="text-white">{profileData.shipping_address?.street || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Ciudad</label>
                  {editingProfile ? (
                    <input 
                      type="text" 
                      value={profileData.shipping_address?.city || ''} 
                      onChange={(e) => setProfileData({...profileData, shipping_address: {...(profileData.shipping_address || {}), city: e.target.value}})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                    />
                  ) : (
                    <p className="text-white">{profileData.shipping_address?.city || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Código postal</label>
                  {editingProfile ? (
                    <input 
                      type="text" 
                      value={profileData.shipping_address?.postal_code || ''} 
                      onChange={(e) => setProfileData({...profileData, shipping_address: {...(profileData.shipping_address || {}), postal_code: e.target.value}})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                    />
                  ) : (
                    <p className="text-white">{profileData.shipping_address?.postal_code || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Provincia</label>
                  {editingProfile ? (
                    <input 
                      type="text" 
                      value={profileData.shipping_address?.province || ''} 
                      onChange={(e) => setProfileData({...profileData, shipping_address: {...(profileData.shipping_address || {}), province: e.target.value}})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                    />
                  ) : (
                    <p className="text-white">{profileData.shipping_address?.province || '-'}</p>
                  )}
                </div>
              </div>

              {editingProfile && (
                <div className="mt-8 p-4 bg-white/5 border border-white/10 rounded-lg">
                  <p className="text-white/60 text-sm">
                    <AlertCircle className="w-4 h-4 inline mr-2 text-yellow-500" />
                    Los cambios que realices deberán ser validados por un administrador antes de hacerse efectivos.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Box Office Reports Tab */}
        {activeTab === 'reports' && cinema && (
          <BoxOfficeReports 
            cinemaId={cinema.cinema_id} 
            onReportSubmitted={() => loadPendingReportsCount(cinema.cinema_id)}
          />
        )}
      </div>

      {/* Cancel Booking Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-xl p-6 max-w-md w-full">
            <h3 className="text-white text-xl font-bold mb-4">Solicitar cancelación</h3>
            <p className="text-white/60 mb-4">
              ¿Estás seguro de que deseas solicitar la cancelación de este pase? 
              El administrador deberá aprobar la solicitud.
            </p>
            <div className="mb-4">
              <label className="block text-white/50 text-sm mb-2">Motivo de la cancelación *</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none resize-none"
                rows={3}
                placeholder="Indica el motivo..."
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowCancelModal(null); setCancelReason(''); }}
                className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
              >
                Volver
              </button>
              <button
                onClick={() => handleCancelRequest(showCancelModal)}
                disabled={submittingChange}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {submittingChange ? 'Enviando...' : 'Solicitar cancelación'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modify Booking Modal */}
      {showModifyModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-white text-xl font-bold mb-4">Solicitar modificación</h3>
            <p className="text-white/60 mb-4">
              Indica las nuevas fechas y/o horarios para las sesiones. 
              El administrador deberá aprobar la solicitud.
            </p>
            
            {/* Info about minimum date */}
            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-amber-200 text-sm">
                La fecha de proyección debe ser al menos <strong>2 días</strong> a partir de hoy.
              </p>
            </div>
            
            <div className="space-y-4 mb-4">
              {modifyData.screenings.map((s, idx) => {
                // Calculate minimum date (today + 2 days)
                const minDate = new Date();
                minDate.setDate(minDate.getDate() + 2);
                const minDateStr = minDate.toISOString().split('T')[0];
                
                return (
                  <div key={idx} className="p-4 bg-white/5 rounded-lg">
                    <p className="text-white/50 text-xs mb-2">Sesión {idx + 1}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-white/50 text-xs mb-1">Fecha</label>
                        <input
                          type="date"
                          value={s.date || ''}
                          min={minDateStr}
                          onChange={(e) => {
                            const newScreenings = [...modifyData.screenings];
                            newScreenings[idx] = { ...newScreenings[idx], date: e.target.value };
                            setModifyData({ ...modifyData, screenings: newScreenings });
                          }}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white focus:border-[#e4002b] focus:outline-none [color-scheme:dark]"
                        />
                      </div>
                      <div>
                        <label className="block text-white/50 text-xs mb-1">Hora</label>
                        <input
                          type="time"
                          value={s.time || ''}
                          onChange={(e) => {
                            const newScreenings = [...modifyData.screenings];
                            newScreenings[idx] = { ...newScreenings[idx], time: e.target.value };
                            setModifyData({ ...modifyData, screenings: newScreenings });
                          }}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white focus:border-[#e4002b] focus:outline-none [color-scheme:dark]"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mb-4">
              <label className="block text-white/50 text-sm mb-2">Motivo de la modificación</label>
              <textarea
                value={modifyData.reason}
                onChange={(e) => setModifyData({ ...modifyData, reason: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none resize-none"
                rows={2}
                placeholder="Opcional..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setShowModifyModal(null); setModifyData({ screenings: [], reason: '' }); }}
                className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
              >
                Volver
              </button>
              <button
                onClick={() => handleModifyRequest(showModifyModal)}
                disabled={submittingChange}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {submittingChange ? 'Enviando...' : 'Solicitar modificación'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Venue Modal */}
      {showAddVenueModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#1a1a1a] rounded-xl border border-white/10 w-full max-w-2xl my-8">
            {/* Modal Header */}
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">Añadir nuevo recinto</h3>
                <p className="text-white/50 text-sm mt-1">El nuevo recinto quedará pendiente de aprobación</p>
              </div>
              <button
                onClick={() => { setShowAddVenueModal(false); resetNewVenueForm(); }}
                className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
              {/* Success Message */}
              {addVenueSuccess && (
                <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-400" />
                  <div>
                    <p className="text-green-400 font-medium">¡Recinto añadido correctamente!</p>
                    <p className="text-green-400/70 text-sm">Quedará pendiente de aprobación por el administrador.</p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {addVenueError && (
                <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  <p className="text-red-400">{addVenueError}</p>
                </div>
              )}

              {!addVenueSuccess && (
                <>
                  {/* Venue Name */}
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Nombre del recinto *</label>
                    <input
                      type="text"
                      value={newVenueData.venue_name}
                      onChange={(e) => updateNewVenueField('venue_name', e.target.value)}
                      placeholder="Ej: Cines Gran Vía"
                      className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:border-[#e4002b] focus:outline-none"
                      data-testid="new-venue-name"
                    />
                  </div>

                  {/* Address Section */}
                  <div className="space-y-4">
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#e4002b]" />
                      Dirección del recinto
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-white/70 text-sm mb-2">Calle y número</label>
                        <input
                          type="text"
                          value={newVenueData.address.street}
                          onChange={(e) => updateNewVenueField('address.street', e.target.value)}
                          placeholder="Ej: Calle Mayor, 15"
                          className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:border-[#e4002b] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Ciudad *</label>
                        <input
                          type="text"
                          value={newVenueData.address.city}
                          onChange={(e) => updateNewVenueField('address.city', e.target.value)}
                          placeholder="Ej: Valencia"
                          className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:border-[#e4002b] focus:outline-none"
                          data-testid="new-venue-city"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Código postal</label>
                        <input
                          type="text"
                          value={newVenueData.address.postal_code}
                          onChange={(e) => updateNewVenueField('address.postal_code', e.target.value)}
                          placeholder="Ej: 46001"
                          className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:border-[#e4002b] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Provincia</label>
                        <input
                          type="text"
                          value={newVenueData.address.province}
                          onChange={(e) => updateNewVenueField('address.province', e.target.value)}
                          placeholder="Ej: Valencia"
                          className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:border-[#e4002b] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">País</label>
                        <input
                          type="text"
                          value={newVenueData.address.country}
                          onChange={(e) => updateNewVenueField('address.country', e.target.value)}
                          className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:border-[#e4002b] focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Rooms Section */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-white font-medium flex items-center gap-2">
                        <Monitor className="w-4 h-4 text-[#e4002b]" />
                        Salas del recinto
                      </h4>
                      <button
                        type="button"
                        onClick={addNewVenueRoom}
                        className="text-sm text-[#e4002b] hover:text-white flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" /> Añadir sala
                      </button>
                    </div>

                    {newVenueData.rooms.map((room, roomIndex) => (
                      <div key={roomIndex} className="bg-black/30 rounded-lg p-4 space-y-4 border border-white/5">
                        <div className="flex items-center justify-between">
                          <span className="text-white/50 text-sm">Sala {roomIndex + 1}</span>
                          {newVenueData.rooms.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeNewVenueRoom(roomIndex)}
                              className="text-red-400 hover:text-red-300 text-sm"
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-white/50 text-xs mb-1">Nombre/Número</label>
                            <input
                              type="text"
                              value={room.room_number}
                              onChange={(e) => updateNewVenueRoom(roomIndex, 'room_number', e.target.value)}
                              placeholder="Sala 1"
                              className="w-full px-3 py-2 bg-black/50 border border-white/10 rounded-lg text-white text-sm focus:border-[#e4002b] focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-white/50 text-xs mb-1">Aforo</label>
                            <input
                              type="number"
                              value={room.seating_capacity}
                              onChange={(e) => updateNewVenueRoom(roomIndex, 'seating_capacity', e.target.value)}
                              placeholder="200"
                              className="w-full px-3 py-2 bg-black/50 border border-white/10 rounded-lg text-white text-sm focus:border-[#e4002b] focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-white/50 text-xs mb-1">Sistema proyección</label>
                            <select
                              value={room.projection_system}
                              onChange={(e) => updateNewVenueRoom(roomIndex, 'projection_system', e.target.value)}
                              className="w-full px-3 py-2 bg-black/50 border border-white/10 rounded-lg text-white text-sm focus:border-[#e4002b] focus:outline-none"
                            >
                              {projectionSystems.map(sys => (
                                <option key={sys} value={sys}>{sys}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-white/50 text-xs mb-1">Sistema sonido</label>
                            <select
                              value={room.sound_system}
                              onChange={(e) => updateNewVenueRoom(roomIndex, 'sound_system', e.target.value)}
                              className="w-full px-3 py-2 bg-black/50 border border-white/10 rounded-lg text-white text-sm focus:border-[#e4002b] focus:outline-none"
                            >
                              {soundSystems.map(sys => (
                                <option key={sys} value={sys}>{sys}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-white/50 text-xs mb-1">Conexión internet</label>
                            <select
                              value={room.internet_connection}
                              onChange={(e) => updateNewVenueRoom(roomIndex, 'internet_connection', e.target.value)}
                              className="w-full px-3 py-2 bg-black/50 border border-white/10 rounded-lg text-white text-sm focus:border-[#e4002b] focus:outline-none"
                            >
                              {internetConnections.map(conn => (
                                <option key={conn} value={conn}>{conn}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            {!addVenueSuccess && (
              <div className="p-6 border-t border-white/10 flex gap-3">
                <button
                  onClick={() => { setShowAddVenueModal(false); resetNewVenueForm(); }}
                  className="flex-1 px-4 py-3 border border-white/20 text-white rounded-lg hover:bg-white/10 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddVenue}
                  disabled={addingVenue}
                  className="flex-1 px-4 py-3 bg-[#e4002b] text-white rounded-lg hover:bg-[#c70024] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-new-venue"
                >
                  {addingVenue ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Añadir recinto
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CinemaBookingPage;
